import { format } from 'date-fns';
import { useMyIppeCalculationMembers } from '@/hooks/members/useMyIppeCalculationMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import { sortFnTags } from '@/lib/member-views/sortRows';
import type { IppeCalculationMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<IppeCalculationMemberRow>;

/**
 * IPPE Calculation — the practice's Active and Suspended members who are 64 and turn 65 within the
 * next 90 days, for scheduling their Medicare Initial Preventive Physical Exam inside its window.
 *
 * The report is legacy's 6 columns below, in `csv.order`, First Name before Last Name as legacy had
 * them. The two suggested dates are the table's own fields.
 */
export const ippeCalculationMembersDataset: MemberDataset<IppeCalculationMemberRow> = {
  key: 'ippe',
  rowKey: (row) => row.id,
  useRows: useMyIppeCalculationMembers,
  alphaKey: (row) => row.lastName,
  // Soonest window first is what the view is for; the server returns last name A–Z.
  defaultSort: [{ id: 'suggestedStart', desc: false }],
  // The same provisional ceiling as All Active; these members are a subset of it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_IPPE_Calculation_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'suggestedStart',
      label: 'memberViews.columns.suggestedStart',
      width: 'sm',
      value: (row) => row.suggestedStartDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 5, label: 'memberViews.csv.columns.suggestedStartDate' },
    },
    {
      id: 'suggestedEnd',
      label: 'memberViews.columns.suggestedEnd',
      width: 'sm',
      value: (row) => row.suggestedEndDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 6, label: 'memberViews.csv.columns.suggestedEndDate' },
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      // No tags is a missing value, so untagged rows sort last both ways, as the design asks.
      value: (row) => (row.priorityTags.length > 0 ? row.priorityTags : null),
      table: true,
      sortable: true,
      sortFn: sortFnTags,
    },
    reportOnly('title', 1, 'memberViews.csv.columns.title', (row) => row.salutation),
    reportOnly('firstName', 2, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('lastName', 3, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('dob', 4, 'memberViews.csv.columns.dob', (row) => row.dateOfBirth, 'date'),
  ],
};
